import React, { ChangeEvent, useState } from 'react';
import Input from '../input/input';
import classes from './form.module.scss';
import { Mutation } from 'react-apollo';
import { Button, CircularProgress } from '@material-ui/core';
import { navigate } from '@reach/router';
import SnackbarMessage from '../snackbar/snackbar';
type ValidatorFn = (arg0: any, arg1?: any) => null | string;

export interface IState {
  [key: string]: {
    label: string;
    config: {};
    value: string;
    validators: ValidatorFn[];
    dependency?: string;
    errorMessage: string;
  };
}

interface IForm {
  state: IState;
  mutation: {};
  submitOptions: {
    keys: string[];
    label: string;
    id: string;
    redirectTo?: string | ((state: IState) => void);
  };
  submitDisabled?: boolean;
  additionalFields?: { [key: string]: string };
  refetchQueries?: [{ query: string }];
}

const Form = (props: IForm) => {
  const { submitDisabled } = props;
  const [state, setState] = useState(props.state);
  const [loadingForm, setLoading] = useState(false);
  const [snackbar, snackbarOpen] = useState({
    show: false,
    message: '',
    status: ''
  });

  function handleInputChange(
    event: ChangeEvent<HTMLInputElement>,
    key: string
  ) {
    const updatedElement = {
      ...state[key],
      value: event.target.value
    };
    setState({ ...state, [key]: updatedElement });
  }

  function getInputErrors(key: string) {
    const input = state[key];
    return input.validators.reduce((errors: any, validator: any) => {
      const args = [input.value];
      if (input.dependency) {
        args.push(state[input.dependency].value);
      }
      const error = validator(...args);
      return error ? [...errors, error] : errors;
    }, []);
  }

  function getFormErrors() {
    return Object.keys(state).reduce((result: string[], key: string) => {
      const errors = getInputErrors(key);
      return [...result, ...errors];
    }, []);
  }

  function updateInputError(key: string, error: string) {
    const updatedElement = {
      ...state[key],
      errorMessage: error
    };
    setState({ ...state, [key]: updatedElement });
  }

  function validateInput(key: string) {
    const errors = getInputErrors(key);
    if (errors.length !== 0) {
      updateInputError(key, errors[0]);
    }
  }

  function validateForm() {
    const copyState = { ...state };
    Object.keys(copyState).forEach(key => {
      const errors = getInputErrors(key);
      copyState[key].errorMessage = errors ? errors[0] : '';
      copyState[key] = state[key];
    });
    setState({ ...copyState });
  }

  function isFormValid() {
    return getFormErrors().length === 0;
  }

  function getValuesForm() {
    const valuesObject: { [key: string]: string } = {};
    for (const key in state) {
      if (state.hasOwnProperty(key)) {
        // Do things here
        if (props.submitOptions.keys.includes(key)) {
          valuesObject[key] = state[key].value;
        }
      }
    }
    return valuesObject;
  }

  function openSnackbar(status: string, message: string) {
    snackbarOpen({ show: true, status, message });
  }

  function sendForm(mutationFn: () => void) {
    validateForm();
    if (isFormValid()) {
      mutationFn();
    }
  }
  return (
    <form
      className={classes.form}
      onSubmit={e => e.preventDefault()}
      noValidate
    >
      {Object.keys(state).map(key => {
        const input = state[key];
        return (
          <Input
            key={key}
            name={key}
            label={input.label}
            value={input.value}
            changed={(e: ChangeEvent<HTMLInputElement>) =>
              handleInputChange(e, key)
            }
            focused={() => updateInputError(key, '')}
            unfocused={() => validateInput(key)}
            error={input.errorMessage}
            {...input.config}
          />
        );
      })}
      <div className={classes.actionButtonContainer}>
        <Mutation
          mutation={props.mutation}
          variables={
            props.additionalFields
              ? {
                  ...props.additionalFields,
                  ...getValuesForm()
                }
              : getValuesForm()
          }
          refetchQueries={props.refetchQueries ? props.refetchQueries : []}
          onCompleted={data => {
            // @ts-ignore
            const { token } = data[Object.keys(data)[0]] || {};
            setLoading(false);
            if (token) {
              localStorage.setItem('token', token);
            }
            if (props.submitOptions.redirectTo) {
              if (typeof props.submitOptions.redirectTo === 'string') {
                navigate(props.submitOptions.redirectTo);
              } else if (typeof props.submitOptions.redirectTo === 'function') {
                props.submitOptions.redirectTo(state);
              }
            }
          }}
          onError={(error: any) => {
            setLoading(false);
            openSnackbar('error', error.graphQLErrors[0].message);
          }}
        >
          {(mutationFn, { loading }) => {
            if (loading) {
              setLoading(true);
            }

            return (
              <div>
                <Button
                  variant="contained"
                  type="submit"
                  color="primary"
                  className={classes.actionButton}
                  disabled={loadingForm || submitDisabled}
                  onClick={() => sendForm(mutationFn)}
                  data-e2e-id={`${props.submitOptions.id}-button`}
                >
                  {props.submitOptions.label}
                  {loadingForm ? (
                    <CircularProgress className={classes.progress} />
                  ) : (
                    ''
                  )}
                </Button>
              </div>
            );
          }}
        </Mutation>
      </div>
      <SnackbarMessage snackbar={snackbar} />
    </form>
  );
};

export default Form;
